import { createApiClient, type ApiClient } from '@hrms/shared';
import { DEFAULT_API_URL, normaliseBaseUrl } from './config';
import { getToken, KEYS, setToken, storage } from './storage';

const unauthorizedListeners = new Set<() => void>();
/** Subscribe to 401 responses (AuthProvider logs out). Returns an unsubscribe fn. */
export const onUnauthorized = (fn: () => void) => {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
};

let baseUrl = DEFAULT_API_URL;

function build(url: string): ApiClient {
  return createApiClient({
    baseUrl: url,
    getToken,
    onUnauthorized: () => {
      if (!getToken()) return;
      void setToken(null);
      unauthorizedListeners.forEach((fn) => fn());
    },
  });
}

let current = build(baseUrl);

/**
 * The typed API client from @hrms/shared. It is a live proxy, so changing the server URL
 * (login screen) takes effect everywhere without re-importing.
 */
export const api: ApiClient = new Proxy({} as ApiClient, {
  get: (_t, key) => (current as unknown as Record<string | symbol, unknown>)[key],
});

export const getBaseUrl = () => baseUrl;

/** Call once at startup, after hydrateStorage(). */
export function initApi() {
  const saved = storage.peek(KEYS.serverUrl);
  baseUrl = saved ? normaliseBaseUrl(saved) : DEFAULT_API_URL;
  current = build(baseUrl);
}

/** Override (or reset with null/'') the server URL; persisted in AsyncStorage. */
export async function setBaseUrl(url: string | null) {
  const n = url ? normaliseBaseUrl(url) : '';
  baseUrl = n || DEFAULT_API_URL;
  current = build(baseUrl);
  await storage.set(KEYS.serverUrl, n && n !== DEFAULT_API_URL ? n : null);
}
